import React, { useEffect, useState } from "react";
import { SubpageTitle } from "../../../components/atoms/text/titles";
import { useQueryBackend } from "../../../utils/requestHelpers";
import { BarChartView, IDataItem } from "../../../components/atoms/charts";
import { DateTime } from "luxon";
import { DateRangePickerPopUp } from "../../../components/molecules/datepickers/dateRangePickerPopUp";
import { TagButton } from "../../../components/molecules/datepickers/tagButtons";
import { MachineLogTable } from "../../reports/machineReport";
import theme from "../../../theme";
import { LabledCheckbox } from "../../../components/molecules/LabledCheckbox";
import ViewBox from "../../../components/atoms/views/viewBox";

interface IErrorWarningItem {
  day: string;
  warningcount: number | undefined;
  errorcount: number;
  criticalcount: number;
}
export const ErrorsAndWarnings = ({ name }: { name?: string | undefined }) => {
  const queryApi = useQueryBackend("Backoffice");
  const [from, setFrom] = useState<DateTime>(DateTime.now().startOf("month"));
  const [to, setTo] = useState<DateTime>(DateTime.now().endOf("month"));
  const [errorsAndWarnings, setErrorsAndWarnings] = useState<Array<IDataItem>>(
    []
  );
  const today = DateTime.now();
  const step = "month";
  const stepper = { month: 1 };
  const todayRollingSeven = today.minus(stepper);
  const lastMonthStart = today.minus(stepper).startOf("month");
  const lastMOnthEnd = today.minus(stepper).endOf("month");
  const [warnings, setWarnings] = useState<boolean>()
  const [noErrorsOrWarnings, setNoErrorsOrWarnings] = useState<boolean>(false)

  useEffect(() => {

    if (!from || !to) return;
    queryApi
      .Get<Array<IErrorWarningItem>>(
        `telemetry/error-warning-bar-chart/${from.toFormat(
          "yyyy-MM-dd"
        )}/and/${to.plus({"day":1}).toFormat("yyyy-MM-dd")}?filterType=${warnings ? "warning" : "no_warning"}`
      )
      .Data((d) => {
        const items: IDataItem[] = [
          ...d.map((i) => {
            const warningCount = i.warningcount ?? 0;
            const errorCount = i.errorcount ?? 0;
            const criticalCount = i.criticalcount ?? 0
            const total = warnings ? warningCount + errorCount + criticalCount : errorCount + criticalCount;

            if (warnings) {
              return {
                name: i.day,
                Warning: warningCount,
                Error: errorCount,
                Critical: criticalCount,
                Total: total > 0 ? total : undefined,
              };
            } else {
              return {
                name: i.day,
                Error: errorCount,
                Critical: criticalCount,
                Total: total > 0 ? total : undefined,
              };
            }
          }),
        ];

        setErrorsAndWarnings(items);
      });
  }, [from, to, warnings]);
  const [open, setOpen] = useState<boolean>(false);
  const presets: TagButton[] = [
    {
      name: "This Month",
      id: "this",
      action: () => {
        setFrom(today.startOf(step));
        setTo(today.endOf("month"));
        setOpen(false);
      },
      default: true,
    },
    {
      name: "Last Month",
      id: "last",
      action: () => {
        setFrom(lastMonthStart);
        setTo(lastMOnthEnd);
        setOpen(false);
      },
    },
    {
      name: "Rolling month",
      id: "rolling",
      action: () => {
        setFrom(todayRollingSeven);
        setTo(today);
        setOpen(false);
      },
    },
  ];

  const [details, setDetails] = useState<{
    from: DateTime | undefined;
    to: DateTime | undefined;
    open;
  }>({ from: undefined, to: undefined, open: false });

  const handle = (event: { name: string, Error: number, Warning: number, Critical: number}) => {
    setDetails({
      from: DateTime.fromISO(event.name),
      to: DateTime.fromISO(event.name).plus({ day: 1 }),
      open: true,
    });
    if ((event.Error ?? 0) === 0 && (event.Warning ?? 0) === 0 && (event.Critical ?? 0) === 0){
      setNoErrorsOrWarnings(true)
      return
    }
    setNoErrorsOrWarnings(false)
  };

  return (
    <ViewBox >
      <SubpageTitle title={name ?? `Errors ${warnings ? "and warnings" : ""}`} />
      <ViewBox extend={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <DateRangePickerPopUp
          startDate={from}
          endDate={to}
          onStartDatePicked={setFrom}
          onEndDatePicked={setTo}
          presets={presets}
          open={open}
          setOpen={setOpen}
        ></DateRangePickerPopUp>
        <LabledCheckbox isChecked={warnings} setIsChecked={setWarnings} label="Include warnings" />
      </ViewBox>
      <div style={{ width: "100%", height: "400px" }}>
        <BarChartView
          handleClick={(event) => handle(event)}
          items={errorsAndWarnings}
          formatX={(s) => DateTime.fromISO(s).toFormat("dd")}
          colorMap={{ Error: theme.palette.error.light, Warning: theme.palette.warning.light, Critical: theme.palette.critical.light }}
          shouldShowGraphLabel
        />
      </div>
      {details.open && details.to && details.from ? (
        <MachineLogTable to={details.to} from={details.from} warnings={warnings} noErrorsOrWarnings={noErrorsOrWarnings}></MachineLogTable>
      ) : (
        <> </>
      )}
    </ViewBox>
  );
};
